import { useTranslation } from 'react-i18next';

import { CheckIcon, IconButton, StarIcon, XIcon, cn, hitTarget44 } from '@natter-co/design-system';

import { IconTile, Pop, Rise } from './FormHeader.styled';

import type { Props } from './types';

/**
 * The feedback dialog's header lockup, from the natalie-templates details dialog: icon tile, title and subtitle,
 * and the close X in the top-right corner. A star tile while answering; a green check tile on the thank-you.
 * On a phone the tile goes and, while answering, the title starts 40px from the top, clear of the X.
 *
 * Ported from `IconTile` and the header markup in the post-event-feedback prototype. Render it in a
 * `Dialog` with `hideHeader`.
 */
export const FormHeader = ({
  title,
  subtitle,
  done = false,
  onClose,
  closeLabel,
  closeDisabled,
  headingRef,
  className,
}: Props) => {
  const { t } = useTranslation('forms', { keyPrefix: 'dialog' });

  return (
    <div className={cn('relative flex items-center gap-4 pr-10', !done && 'max-sm:pt-5!', className)}>
      <IconTile aria-hidden data-testid="form-header-tile" data-done={done || undefined}>
        {/* Keyed, so the check mounts fresh and pops in. */}
        <Pop key={done ? 'done' : 'star'} data-animate={done || undefined}>
          {done ? (
            <CheckIcon size={24} data-testid="form-header-check" />
          ) : (
            <StarIcon size={24} data-testid="form-header-star" />
          )}
        </Pop>
      </IconTile>
      <Rise key={done ? 'done' : 'ask'} data-animate={done || undefined} className="flex min-w-0 flex-col gap-0.5">
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="m-0 text-heading-sm text-balance text-[var(--foreground-primary)] outline-none"
        >
          {title}
        </h2>
        {subtitle && <p className="m-0 text-body-md text-pretty text-[var(--foreground-secondary)]">{subtitle}</p>}
      </Rise>
      <IconButton
        appearance="tertiary"
        size="xs"
        aria-label={closeLabel ?? t('close')}
        disabled={closeDisabled}
        className={cn(hitTarget44.from32, 'absolute! top-0 right-0')}
        onClick={onClose}
      >
        <XIcon />
      </IconButton>
    </div>
  );
};
